import {BaseDialogViewModel} from "../../common/BaseDialogViewModel";
import {TracedoManager} from "../../../model/TracedoManager";
import {inject, injectable} from "tsyringe";
import {Client} from "../../../../tracejs/src/net/jsonrpc/Client";
import {Tracedo} from "../../../entities/Tracedo";
import {Checkpoint} from "../../../entities/Checkpoint";
import {CheckpointFactory} from "../../../model/EditTracedo/CheckpointFactory";
import {h} from "../../../../tracejs/src/utils/JSXFactory";
import {TracedoParentInfo} from "../../../entities/TracedoParentInfo";

interface DisplayableCheckpoint {
    checkpoint: Checkpoint;
    displayName: string;
}

@injectable()
export class CreateSubtransportDialogView extends BaseDialogViewModel<any> {

    private originCheckpoints: KnockoutObservableArray<DisplayableCheckpoint> = ko.observableArray();
    private destinationCheckpoints: KnockoutObservableArray<DisplayableCheckpoint> = ko.observableArray();
    private parentNumber: string = '';
    private selectedOriginCheckpoint: KnockoutObservable<Checkpoint> = ko.observable(
        CheckpointFactory.makeCheckpoint()
    );
    private selectedDestinationCheckpoint: KnockoutObservable<Checkpoint> = ko.observable(
        CheckpointFactory.makeCheckpoint()
    );

    constructor(@inject(Client) rpc: Client, @inject(TracedoManager) private tracedoManager: TracedoManager) {
        super(rpc);
    }

    async startup(): Promise<any> {
        await super.startup();

        if (this.settings.tracedoId === null) throw new Error("Missing tracedoId");
        const tracedo: Tracedo = await this.tracedoManager.getTracedoWithId(this.settings.tracedoId);

        this.parentNumber = tracedo.myNumber;

        this.originCheckpoints.push(
            this.makeDisplayableCheckpoint(CheckpointFactory.makeOriginCheckpoint(tracedo, []))
        );

        for (const checkpoint of tracedo.checkpoints) {
            this.originCheckpoints.push(this.makeDisplayableCheckpoint(checkpoint));
            this.destinationCheckpoints.push(this.makeDisplayableCheckpoint(checkpoint));
        }

        this.destinationCheckpoints.push(
            this.makeDisplayableCheckpoint(CheckpointFactory.makeDestinationCheckpoint(tracedo, []))
        );
    }

    private makeDisplayableCheckpoint(checkpoint: Checkpoint): DisplayableCheckpoint {
        const checkpointName = checkpoint.name ? checkpoint.name : `${checkpoint.posIndex + 1}. zastávka`;
        return {
            checkpoint: checkpoint,
            displayName: `${checkpointName} - ${checkpoint.addressName}`
        };
    }

    public getTracedoParentInfo(): TracedoParentInfo {
        return {
            startCheckpoint: this.selectedOriginCheckpoint(),
            startCheckpointId: this.selectedOriginCheckpoint().id,
            startCheckpointType: this.selectedOriginCheckpoint().id == null ? 'origin' : 'checkpoint',

            endCheckpoint: this.selectedDestinationCheckpoint(),
            endCheckpointId: this.selectedDestinationCheckpoint().id,
            endCheckpointType: this.selectedDestinationCheckpoint().id == null ? 'destination' : 'checkpoint',
            parentNumber: this.parentNumber
        }
    }

    public dialogTemplate = (): HTMLElement => (
        <div>
            <div className="row mb-2">
                <label className="inline col-md-4 col-form-label"
                       data-bind="uniqueFor: 'chooseOriginCheckpoints', i18n: 'common.captions.chooseOriginCheckpoints.title'"></label>
                <div className="col-md-8">
                    <select data-bind="uniqueId: 'originCheckpoints',
                                       options: $root.originCheckpoints,
                                       optionsText: 'displayName',
                                       optionsValue: 'checkpoint',
                                       optionsCaption: i18n.t('common.captions.chooseOriginCheckpoints.label'),
                                       value: $root.selectedOriginCheckpoint"
                            className="form-select"></select>
                </div>
            </div>

            <div className="row mb-2">
                <label className="inline col-md-4 col-form-label"
                       data-bind="uniqueFor: 'chooseDestinationCheckpoints', i18n: 'common.captions.chooseDestinationCheckpoints.title'"></label>
                <div className="col-md-8">
                    <select data-bind="uniqueId: 'destinationCheckpoints',
                                       options: $root.destinationCheckpoints,
                                       optionsText: 'displayName',
                                       optionsValue: 'checkpoint',
                                       optionsCaption: i18n.t('common.captions.chooseDestinationCheckpoints.label'),
                                       value: $root.selectedDestinationCheckpoint"
                            className="form-select"></select>
                </div>
            </div>
        </div>
    )
}
